<template>
	<div class="rs-stagerow" :data-sid="entry.id" v-test="'project-share-dialog-staged-row'">
		<div class="top">
			<span class="rs-savatar">{{ initials }}</span>
			<div class="rs-sinfo">
				<div class="sn">
					{{ entry.name }}
					<span v-if="entry.teammate" class="rs-tflag">on your team</span>
					<span v-else-if="entry.contact" class="rs-cflag">contact</span>
					<!-- LSP-18909: every staged row now carries a tag. What was left untagged is the one
					     case that is neither — an address or number typed in that matches nobody in the
					     directory and nobody on the team. Labelling it honestly ("new") is what removes
					     the "tags are applied selectively" reading; silently omitting the tag made an
					     unrecognised invitee look identical to a bug. -->
					<span v-else class="rs-nflag" v-test="'project-share-dialog-staged-new-tag'">new</span>
					<span v-if="entry.rateLimited" class="rs-rflag" v-test="'project-share-dialog-staged-ratelimited-tag'"
						>wait to retry</span
					>
				</div>
				<div class="se">
					{{ displayIdentifier }}<template v-if="!entry.teammate && entry.phoneOnly"> · invited by text</template>
				</div>
			</div>
			<button
				class="rs-x"
				type="button"
				title="Remove"
				v-test="'project-share-dialog-staged-remove'"
				@click="emit('remove')"
				v-html="shareIcons.x"></button>
		</div>

		<div v-if="showCountryCode" class="rs-ccrow">
			<span class="rs-clabel">Country code</span>
			<select class="rs-ccsel" :value="entry.cc" @change="onCcChange">
				<option v-for="code in countryCodes" :key="code[0]" :value="code[0]">{{ code[1] }} · {{ code[0] }}</option>
			</select>
		</div>

		<div class="rs-choose">
			<span class="rs-clabel">Choose access:</span>
			<div class="rs-seg">
				<button
					v-for="option in roleOptions"
					:key="option.value"
					class="rs-segbtn"
					:class="{ on: entry.role === option.value }"
					type="button"
					v-test="'project-share-dialog-role-' + option.value"
					@click="emit('set-role', option.value)">
					{{ option.label }}
				</button>
			</div>
		</div>

		<template v-if="entry.role">
			<div v-if="!entry.teammate" class="rs-rdesc">{{ roleDescription }}</div>
			<div class="rs-stagenote">
				<label class="rs-snlabel">Add a note if you want</label>
				<textarea
					class="rs-note"
					:class="{ 'rs-note--over': noteOverLimit }"
					v-test="'project-share-dialog-staged-note'"
					placeholder="Add instructions or a message… e.g. “Open the link, create a password, and fill in the plumbing checklist.”"
					:value="entry.note"
					@input="onNoteInput"></textarea>
				<div class="rs-sncount" :class="{ 'rs-sncount--over': noteOverLimit }" v-test="'project-share-dialog-staged-note-count'">
					{{ noteCount }}/{{ NOTE_MAX }}
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import { shareIcons } from "./shareIcons";
	import { countCharacters } from "@/helpers/utilities";
	import { phoneCountryOptionsPinned } from "@/helpers/phone";

	// Matches the API's note cap — warn (red counter), never hard-cap the textarea (repo CLAUDE.md).
	// The server truncates past this, so surface it before the sender ships an over-length note.
	const NOTE_MAX = 1000;

	interface StagedEntry {
		id: string;
		name: string;
		email?: string;
		phone?: string;
		phoneOnly?: boolean;
		teammate?: boolean;
		contact?: boolean;
		rateLimited?: boolean;
		company?: string;
		role?: string | null;
		cc: string;
		note: string;
	}

	const props = defineProps<{
		entry: StagedEntry;
	}>();

	const emit = defineEmits<{
		"set-role": [role: string];
		"update-note": [note: string];
		"update-cc": [cc: string];
		remove: [];
	}>();

	// LSP-17773 (review): full list from the shared libphonenumber-backed helper — this was a
	// hardcoded 8-entry copy of the same list ShareActionDialog carried. The row's own cc (the
	// dialog stamps the ORG default) is pinned first so the select never renders blank.
	const countryCodes = computed<[string, string][]>(() => phoneCountryOptionsPinned(props.entry.cc, props.entry.cc));

	const roleOptions = [
		{ value: "viewer", label: "Viewer" },
		{ value: "editor", label: "Editor" }
	];

	const roleDescriptions: Record<string, string> = {
		viewer: "Can view files — can't change anything or upload.",
		editor: "Can add media, comments, sheets & fill out checklists. Can't create tags, contacts or checklists, or reshare."
	};

	const roleDescription = computed(() => (props.entry.role ? roleDescriptions[props.entry.role] || "" : ""));

	const noteCount = computed(() => countCharacters(props.entry.note || ""));
	const noteOverLimit = computed(() => noteCount.value > NOTE_MAX);

	const rawNumber = computed(() => (props.entry.phone || "").trim());

	const displayIdentifier = computed(() => {
		if (!props.entry.phoneOnly) return props.entry.email || rawNumber.value;
		const raw = rawNumber.value;
		if (raw[0] === "+") return raw;
		return `${props.entry.cc || "+1"} ${raw}`;
	});

	// Bare (non-E.164) number → show the country-code selector so the sender can fix it.
	const showCountryCode = computed(() => !!props.entry.phoneOnly && rawNumber.value[0] !== "+");

	const initials = computed(() => {
		const parts = (props.entry.name || "").trim().split(/\s+/).filter(Boolean);
		if (!parts.length) return "?";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	});

	function onNoteInput(event: Event) {
		emit("update-note", (event.target as HTMLTextAreaElement).value);
	}
	function onCcChange(event: Event) {
		emit("update-cc", (event.target as HTMLSelectElement).value);
	}
</script>

<style scoped>
	/* Ported verbatim from design-ref/lp-real.css (.rs-stagerow / .rs-seg / .rs-note). */
	.rs-stagerow {
		--blue: #2563eb;
		--ink: #111827;
		--ink2: #374151;
		--ink3: #6b7280;
		--ink4: #9ca3af;
		--line: #d5dae2;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
		border: 1px solid var(--line);
		border-radius: 12px;
		padding: 12px 12px 12px 14px;
		margin-bottom: 10px;
		background: #fff;
		color: var(--ink);
	}
	.rs-stagerow.confirmed {
		border-color: #bbf7d0;
		background: #f6fef9;
	}
	.rs-stagerow .top {
		display: flex;
		align-items: center;
		gap: 12px;
	}
	.rs-savatar {
		width: 38px;
		height: 38px;
		border-radius: 999px;
		background: #bfe0f5;
		color: #0f3d5c;
		font-weight: 700;
		font-size: 13px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.rs-sinfo {
		flex: 1;
		min-width: 0;
	}
	.rs-sinfo .sn {
		font-size: 14.5px;
		font-weight: 700;
		display: flex;
		align-items: center;
		gap: 7px;
		/* A long name is a flex item here alongside the tag; let it wrap and keep the tag intact
		   rather than letting either push out of the card. */
		flex-wrap: wrap;
		min-width: 0;
		overflow-wrap: anywhere;
	}
	.rs-sinfo .se {
		font-size: 12.5px;
		color: var(--ink3);
		margin-top: 2px;
		/* A long email is one unbreakable token, so `min-width: 0` on .rs-sinfo is not enough on its
		   own — with nowhere to wrap it rendered straight out through the right edge of the row (and
		   past the dialog). `anywhere` lets it break mid-token, which keeps the whole address
		   readable; truncating would hide it, and there is no title attribute to recover it from. */
		overflow-wrap: anywhere;
	}
	.rs-cflag {
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 700;
		color: #6d28d9;
		background: #ede9fe;
		border-radius: 5px;
		padding: 2px 6px;
		text-transform: uppercase;
		letter-spacing: 0.03em;
	}
	.rs-tflag {
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 700;
		color: #047857;
		background: #ecfdf5;
		border-radius: 5px;
		padding: 2px 6px;
		text-transform: uppercase;
		letter-spacing: 0.03em;
	}
	/* LSP-18909: neutral slate, deliberately quieter than the contact (violet) and team (green)
	   tags — it marks an invitee we have no record of, not a status worth drawing the eye to. */
	.rs-nflag {
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 700;
		color: #475569;
		background: #f1f5f9;
		border-radius: 5px;
		padding: 2px 6px;
		text-transform: uppercase;
		letter-spacing: 0.03em;
	}
	.rs-rflag {
		flex-shrink: 0;
		font-size: 10px;
		font-weight: 700;
		color: rgb(var(--v-theme-error));
		background: rgba(var(--v-theme-error), 0.08);
		border-radius: 5px;
		padding: 2px 6px;
		text-transform: uppercase;
		letter-spacing: 0.03em;
	}
	.rs-x {
		width: 32px;
		height: 32px;
		border: 0;
		background: transparent;
		color: var(--ink3);
		border-radius: 999px;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.rs-x:hover {
		background: #fee2e2;
		color: #b91c1c;
	}
	.rs-ccrow {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 11px;
		padding-left: 50px;
		flex-wrap: wrap;
	}
	.rs-ccsel {
		height: 34px;
		border: 1px solid #cbd5e1;
		border-radius: 8px;
		padding: 0 10px;
		font: inherit;
		font-size: 13px;
		color: var(--ink);
		background: #fff;
	}
	.rs-choose {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 12px;
		padding-left: 50px;
		flex-wrap: wrap;
	}
	.rs-clabel {
		font-size: 12.5px;
		color: var(--ink2);
		font-weight: 600;
	}
	.rs-seg {
		display: inline-flex;
		border: 1px solid #cbd5e1;
		border-radius: 9px;
		overflow: hidden;
	}
	.rs-segbtn {
		border: 0;
		background: #fff;
		font-size: 13px;
		font-weight: 600;
		color: var(--ink2);
		padding: 8px 16px;
		cursor: pointer;
		border-right: 1px solid #e5e7eb;
	}
	.rs-segbtn:last-child {
		border-right: 0;
	}
	.rs-segbtn:hover {
		background: #f3f4f6;
	}
	.rs-segbtn.on {
		background: var(--blue);
		color: #fff;
	}
	.rs-rdesc {
		font-size: 12px;
		color: var(--ink3);
		line-height: 1.5;
		margin: 8px 0 0;
		padding-left: 50px;
	}
	.rs-stagenote {
		margin-top: 12px;
	}
	.rs-snlabel {
		display: block;
		font-size: 12.5px;
		font-weight: 700;
		color: var(--ink);
		margin-bottom: 6px;
	}
	.rs-note {
		width: 100%;
		box-sizing: border-box;
		min-height: 56px;
		max-height: 18.75rem;
		overflow-y: auto;
		resize: vertical;
		border: 1px solid #cbd5e1;
		border-radius: 8px;
		padding: 10px 12px;
		font: inherit;
		font-size: 13.5px;
		color: var(--ink);
		line-height: 1.5;
	}
	.rs-note:focus {
		outline: 0;
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
	}
	.rs-note--over {
		border-color: #dc2626;
	}
	.rs-note--over:focus {
		border-color: #dc2626;
		box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
	}
	.rs-sncount {
		margin-top: 4px;
		text-align: right;
		font-size: 11.5px;
		color: var(--ink4);
	}
	.rs-sncount--over {
		color: #dc2626;
		font-weight: 700;
	}
	.rs-note::placeholder {
		color: var(--ink4);
	}
</style>
